import { useEffect, useMemo, useState, type CSSProperties } from "react";
import {
  FORTRESS_CARDS_CONFIG,
  type CreateFortressCardsVersionRequest,
  type CreateFortressCardsVersionResponse,
  type FortressCard,
  type FortressCardsCataloguesResponse,
  type FortressPattern,
  type ResourceAmount,
  type WinTiming,
} from "@fortress/shared";
import { httpBase } from "../net/origin";
import { ensureValidSession, type ValidSession } from "../auth/session";
import {
  addC,
  BASE,
  buildGrid,
  EditableGrid,
  keyC,
  mapToPattern,
  mirrorPattern,
  PatternDiagram,
} from "./fortressPattern";
import { FortressCardFace } from "./FortressCardFace";
import { ResourceIcon, RESOURCE_KINDS } from "../resources";

// `/edit/cards` — the DB card editor. Loads every versioned `fortress_cards`
// catalogue from Mongo (latest first). The editing BASE is the latest version
// — or, when the DB holds no catalogues, the bundled code-default fallback
// cards. Cards render as compact in-game faces (win fortresses on the dark
// face, like the library); an EDIT button expands a card into an inline editor
// for its pattern, cost, description and — on win cards — win timing. Edited
// cards are published together as a brand-new version: existing versions are
// NEVER overwritten, the server assigns the next version number and stamps
// `lastUpdatedMs` on each card that actually changed. Publishing requires a
// signed-in admin account (`serverConfig.adminEmails`); browsing is open like
// the other library pages.

type CatalogueWire = FortressCardsCataloguesResponse["catalogues"][number];

/** One card's editable fields, as currently held in the editor. */
interface CardEdit {
  description: string;
  cost: ResourceAmount;
  pattern: FortressPattern;
  winTiming?: WinTiming;
}

/** A card's stored pattern → editor state (node key → shape | "marker"). */
function patternToMap(card: FortressCard): Map<string, string> {
  const m = new Map<string, string>();
  for (const b of card.pattern.buildings) m.set(keyC(addC(BASE, b.offset)), b.shape);
  for (const mk of card.pattern.markers) m.set(keyC(addC(BASE, mk)), "marker");
  return m;
}

/** Translation-independent pattern signature (building order is not meaningful). */
function patternSig(p: FortressPattern): string {
  const c = (v: { q: number; r: number; s: number }) => `${v.q},${v.r},${v.s}`;
  return [
    ...p.buildings.map((b) => `${b.shape}@${c(b.offset)}`).sort(),
    ...p.markers.map((m) => `x@${c(m)}`).sort(),
  ].join("|");
}

const WIN_TIMING_OPTIONS: { value: "" | WinTiming; label: string }[] = [
  { value: "", label: "none" },
  { value: "immediate", label: "immediate (IMM)" },
  { value: "start-of-turn", label: "start of turn (SOT)" },
  { value: "end-of-round", label: "end of round (EOR)" },
];

const DEFAULT_WIN_CARDS = FORTRESS_CARDS_CONFIG.winCards as FortressCard[];
const DEFAULT_REGULAR_CARDS =
  FORTRESS_CARDS_CONFIG.regularCards as FortressCard[];

/** Win cards edit on the same dark palette their card face uses in-game. */
function editorTheme(isWin: boolean) {
  return {
    bg: isWin ? "#1a1a22" : "#fff",
    panelBg: isWin ? "#121218" : "#faf6ea",
    border: isWin ? "#3d3d48" : "#ddd5c2",
    panelBorder: isWin ? "#3d3d48" : "#e9e1cc",
    text: isWin ? "#e8e4dc" : "#2b2620",
    label: isWin ? "#a8a8b2" : "#888",
    inputBg: isWin ? "#121218" : "#fbf7ec",
    inputBorder: isWin ? "#4a4a52" : "#ccc3ac",
    btnBg: isWin ? "#2a2a33" : "#f6f2e6",
  };
}

function EditableCard({
  card,
  grid,
  onEdit,
}: {
  card: FortressCard;
  grid: ReturnType<typeof buildGrid>;
  /** Reports the card's current editable fields whenever they change; `null`
   * when the card is back to its base state. */
  onEdit: (cardId: string, edit: CardEdit | null) => void;
}) {
  const isWin = card.kind === "win";
  const t = editorTheme(isWin);
  const [editing, setEditing] = useState(false);
  const [shapes, setShapes] = useState<Map<string, string>>(() => patternToMap(card));
  const [description, setDescription] = useState(card.description);
  const [cost, setCost] = useState<ResourceAmount>(() => [...card.cost]);
  const [winTiming, setWinTiming] = useState<WinTiming | undefined>(card.winTiming);

  const cycle = (key: string) =>
    setShapes((prev) => {
      const next = new Map(prev);
      const cur = next.get(key);
      const order = [undefined, "scout", "barracks", "mine", "wild", "marker"];
      const val = order[(order.indexOf(cur) + 1) % order.length];
      if (!val) next.delete(key);
      else next.set(key, val);
      return next;
    });

  const pattern = useMemo(
    () => mapToPattern(shapes, card.pattern.bridges),
    [shapes, card.pattern.bridges],
  );
  const dirty =
    description !== card.description ||
    cost.some((n, i) => n !== card.cost[i]) ||
    winTiming !== card.winTiming ||
    patternSig(pattern) !== patternSig(card.pattern);

  useEffect(() => {
    onEdit(
      card.id,
      dirty ? { description, cost, pattern, winTiming } : null,
    );
    // `onEdit` is a stable parent setter; reporting on value changes only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, description, cost, pattern, winTiming, card.id]);

  const reset = () => {
    setShapes(patternToMap(card));
    setDescription(card.description);
    setCost([...card.cost]);
    setWinTiming(card.winTiming);
  };

  // The card face always previews the CURRENT edit state, so the compact tile
  // reflects pending changes before they're published.
  const preview: FortressCard = useMemo(() => {
    const { winTiming: _t, ...rest } = card;
    return {
      ...rest,
      description,
      cost,
      pattern,
      ...(winTiming ? { winTiming } : {}),
    };
  }, [card, description, cost, pattern, winTiming]);

  const footer = (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6 }}>
      <button
        type="button"
        onClick={() => setEditing((v) => !v)}
        style={{
          ...btn,
          fontWeight: 700,
          letterSpacing: 0.4,
          background: t.btnBg,
          border: `1px solid ${t.inputBorder}`,
          color: t.text,
        }}
      >
        {editing ? "DONE" : "EDIT"}
      </button>
      {dirty && (
        <>
          <span style={{ fontSize: 11, color: "#c9a227", fontWeight: 700 }}>
            edited
          </span>
          <button
            type="button"
            onClick={reset}
            style={{
              ...btn,
              background: t.btnBg,
              border: `1px solid ${t.inputBorder}`,
              color: t.text,
            }}
          >
            reset
          </button>
        </>
      )}
      <span style={{ marginLeft: "auto", fontSize: 11, color: "#999" }}>
        {card.lastUpdatedMs
          ? `updated ${new Date(card.lastUpdatedMs).toLocaleDateString()}`
          : ""}
      </span>
    </div>
  );

  if (!editing) {
    return (
      <div style={{ display: "flex", flexDirection: "column" }}>
        <FortressCardFace card={preview} style={{ flex: 1 }} />
        {footer}
      </div>
    );
  }

  return (
    <div
      style={{
        gridColumn: "1 / -1", // an open editor gets the full row
        border: dirty ? "1px solid #c9a227" : `1px solid ${t.border}`,
        borderRadius: 12,
        padding: 14,
        background: t.bg,
        color: t.text,
        boxShadow: isWin
          ? "0 1px 3px rgba(0,0,0,0.35)"
          : "0 1px 3px rgba(0,0,0,0.06)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <strong style={{ fontSize: 15 }}>{card.name}</strong>
        <span style={{ fontSize: 12, color: t.label }}>
          {isWin ? "win" : "regular"} · {card.id}
        </span>
      </div>

      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 11, color: t.label, marginBottom: 2 }}>
            pattern (click a node to cycle)
          </div>
          <EditableGrid grid={grid} shapes={shapes} onCycle={cycle} dark={isWin} />
        </div>
        <div>
          <div style={{ fontSize: 11, color: t.label, marginBottom: 2 }}>mirror</div>
          <div
            style={{
              background: t.panelBg,
              border: `1px solid ${t.panelBorder}`,
              borderRadius: 8,
              padding: 6,
            }}
          >
            <PatternDiagram
              pattern={mirrorPattern(pattern)}
              maxW={180}
              maxH={170}
              dark={isWin}
            />
          </div>
        </div>

        <div style={{ flex: 1, minWidth: 280, display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <div style={{ fontSize: 11, color: t.label, marginBottom: 4 }}>cost</div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              {RESOURCE_KINDS.map((kind, i) => (
                <label
                  key={kind}
                  style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12 }}
                >
                  <ResourceIcon kind={kind} size={14} />
                  <input
                    type="number"
                    min={0}
                    value={cost[i]}
                    onChange={(e) =>
                      setCost((prev) => {
                        const next = [...prev] as ResourceAmount;
                        next[i] = Math.max(0, Math.floor(Number(e.target.value) || 0));
                        return next;
                      })
                    }
                    style={{
                      ...numInput,
                      background: t.inputBg,
                      border: `1px solid ${t.inputBorder}`,
                      color: t.text,
                    }}
                  />
                </label>
              ))}
            </div>
          </div>

          {isWin && (
            <div>
              <div style={{ fontSize: 11, color: t.label, marginBottom: 4 }}>
                win timing
              </div>
              <select
                value={winTiming ?? ""}
                onChange={(e) =>
                  setWinTiming((e.target.value || undefined) as WinTiming | undefined)
                }
                style={{
                  padding: "4px 8px",
                  borderRadius: 6,
                  background: t.inputBg,
                  border: `1px solid ${t.inputBorder}`,
                  color: t.text,
                  fontSize: 13,
                }}
              >
                {WIN_TIMING_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <div style={{ fontSize: 11, color: t.label, marginBottom: 4 }}>
              description
            </div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              style={{
                width: "100%",
                boxSizing: "border-box",
                flex: 1,
                padding: 8,
                border: `1px solid ${t.inputBorder}`,
                borderRadius: 6,
                background: t.inputBg,
                color: t.text,
                fontSize: 13,
                fontFamily: "inherit",
                resize: "vertical",
              }}
            />
          </div>
        </div>
      </div>

      {footer}
    </div>
  );
}

function ReadOnlySection({
  title,
  subtitle,
  cards,
}: {
  title: string;
  subtitle?: string;
  cards: FortressCard[];
}) {
  return (
    <details style={{ marginBottom: 22 }}>
      <summary style={{ cursor: "pointer", fontSize: 16, fontWeight: 600 }}>
        {title}{" "}
        <span style={{ color: "#888", fontWeight: 400, fontSize: 13 }}>
          ({cards.length} cards{subtitle ? ` · ${subtitle}` : ""}) — read-only
        </span>
      </summary>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
          gap: 16,
          marginTop: 12,
        }}
      >
        {cards.map((c) => (
          <FortressCardFace key={c.id} card={c} />
        ))}
      </div>
    </details>
  );
}

export function CardsEditor() {
  const grid = useMemo(() => buildGrid(4), []);
  const [catalogues, setCatalogues] = useState<CatalogueWire[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [session, setSession] = useState<ValidSession | null | undefined>(undefined);
  const [edits, setEdits] = useState<Map<string, CardEdit>>(new Map());
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedVersion, setSavedVersion] = useState<string | null>(null);

  const loadCatalogues = () =>
    fetch(`${httpBase()}/catalogues/fortress-cards`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<FortressCardsCataloguesResponse>;
      })
      .then((data) => setCatalogues(data.catalogues))
      .catch((err: unknown) =>
        setLoadError(err instanceof Error ? err.message : "failed to load"),
      );

  useEffect(() => {
    void loadCatalogues();
    void ensureValidSession().then(setSession);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const latest = catalogues?.[0];
  // The editing base: the latest DB version, else the code-default fallback.
  const baseWin: FortressCard[] = latest ? latest.winCards : DEFAULT_WIN_CARDS;
  const baseRegular: FortressCard[] = latest
    ? latest.regularCards
    : DEFAULT_REGULAR_CARDS;
  const baseVersion = latest?.version ?? "";
  const nextVersion = latest ? String((Number(latest.version) || 0) + 1) : "1";

  const handleEdit = (cardId: string, edit: CardEdit | null) =>
    setEdits((prev) => {
      const had = prev.has(cardId);
      if (!edit && !had) return prev;
      const next = new Map(prev);
      if (edit) next.set(cardId, edit);
      else next.delete(cardId);
      return next;
    });

  const applyEdits = (cards: FortressCard[]): FortressCard[] =>
    cards.map((c) => {
      const e = edits.get(c.id);
      if (!e) return c;
      const { winTiming: _t, ...rest } = c;
      return {
        ...rest,
        description: e.description,
        cost: e.cost,
        pattern: e.pattern,
        ...(e.winTiming ? { winTiming: e.winTiming } : {}),
      };
    });

  const publish = async () => {
    setSaving(true);
    setSaveError(null);
    setSavedVersion(null);
    try {
      const fresh = await ensureValidSession();
      setSession(fresh);
      if (!fresh) {
        setSaveError("You must be signed in (as an admin) to publish.");
        return;
      }
      const body: CreateFortressCardsVersionRequest = {
        baseVersion,
        winCards: applyEdits(baseWin),
        regularCards: applyEdits(baseRegular),
      };
      const res = await fetch(`${httpBase()}/catalogues/fortress-cards`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${fresh.tokens.accessToken}`,
        },
        body: JSON.stringify(body),
      });
      if (res.status === 401) throw new Error("session expired — sign in again");
      if (res.status === 403) {
        throw new Error("this account is not whitelisted as an admin");
      }
      if (res.status === 409) {
        throw new Error(
          "a newer version was published meanwhile — reload to rebase your edits",
        );
      }
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { version } = (await res.json()) as CreateFortressCardsVersionResponse;
      setSavedVersion(version);
      setEdits(new Map());
      await loadCatalogues(); // the new version becomes the editing base
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : "publish failed");
    } finally {
      setSaving(false);
    }
  };

  const baseCards = [...baseWin, ...baseRegular];

  return (
    <div
      style={{
        // Same self-scroll trick as the other library pages: #root is locked
        // to height:100%, so this page is its own scroll container.
        height: "100vh",
        overflowY: "auto",
        background: "#f2ecda",
        color: "#2b2620",
        padding: "24px 28px",
        fontFamily: "system-ui, sans-serif",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          gap: 14,
          marginBottom: 10,
          flexWrap: "wrap",
        }}
      >
        <h1 style={{ margin: 0, fontSize: 24 }}>Card Editor (DB)</h1>
        {catalogues && (
          <span style={{ color: "#777" }}>
            {catalogues.length} version{catalogues.length === 1 ? "" : "s"} in DB
          </span>
        )}
        <span style={{ marginLeft: "auto", fontSize: 13, color: "#777" }}>
          {session === undefined
            ? "checking session…"
            : session
              ? `signed in as ${session.user.username ?? session.user.email ?? session.user.userId}`
              : (
                  <a href="/auth?returnTo=/edit/cards" style={{ color: "#3f7fd0" }}>
                    sign in to publish
                  </a>
                )}
        </span>
        <a href="/library/fortresses" style={{ color: "#3f7fd0" }}>
          card library
        </a>
        <a href="/" style={{ color: "#3f7fd0" }}>
          ← back to game
        </a>
      </div>

      <p style={{ color: "#555", marginTop: 0, fontSize: 13, maxWidth: 880 }}>
        Hit <strong>EDIT</strong> on a card to change its pattern (click nodes
        to cycle empty → scout → barracks → mine → wild → marker), cost,
        description and — on win fortresses — win timing, then publish
        everything as a <strong>new</strong> catalogue version. Existing
        versions are never overwritten. The server stamps{" "}
        <code>lastUpdatedMs</code> on each card that changed. Publishing
        requires a signed-in admin account. Descriptions may describe behaviour
        the code doesn't implement yet; that's expected. Bridges can't be
        edited here and pass through unchanged.
      </p>

      {loadError && (
        <p style={{ color: "#c0392b" }}>
          Failed to load catalogues from the server: {loadError}
        </p>
      )}
      {!loadError && catalogues === null && <p style={{ color: "#777" }}>Loading…</p>}

      {catalogues !== null && (
        <>
          <div
            style={{
              position: "sticky",
              top: 0,
              zIndex: 2,
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "10px 14px",
              margin: "0 0 16px",
              background: "#fffdf6",
              border: "1px solid #ddd5c2",
              borderRadius: 10,
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
              flexWrap: "wrap",
            }}
          >
            <strong style={{ fontSize: 14 }}>
              {edits.size === 0
                ? "No edits yet"
                : `${edits.size} card${edits.size === 1 ? "" : "s"} edited`}
            </strong>
            <span style={{ fontSize: 12.5, color: "#777" }}>
              base:{" "}
              {latest ? `version ${latest.version} (latest)` : "code-default fallback"}{" "}
              → publishes as version {nextVersion}
            </span>
            <button
              type="button"
              onClick={() => void publish()}
              disabled={edits.size === 0 || saving || !session}
              style={{
                ...btn,
                marginLeft: "auto",
                padding: "6px 16px",
                fontWeight: 700,
                fontSize: 13,
                opacity: edits.size === 0 || saving || !session ? 0.5 : 1,
              }}
            >
              {saving ? "publishing…" : `Publish as version ${nextVersion}`}
            </button>
          </div>

          {saveError && <p style={{ color: "#c0392b" }}>⚠ {saveError}</p>}
          {savedVersion && (
            <p style={{ color: "#2d7a2d" }}>
              Published version {savedVersion} — it is now the editing base.
            </p>
          )}

          <section style={{ marginBottom: 26 }}>
            <h2 style={{ fontSize: 18, margin: "0 0 4px" }}>
              {latest ? `Version ${latest.version}` : "Default fallback cards"}{" "}
              <span style={{ color: "#888", fontWeight: 400, fontSize: 13 }}>
                ({latest ? "latest" : "no versions in DB — editing the bundled defaults"}
                {" · "}
                {baseCards.length} cards · editable)
              </span>
            </h2>
            {latest?.updatedAt && (
              <div style={{ fontSize: 12, color: "#999", marginBottom: 10 }}>
                catalogue created {new Date(latest.updatedAt).toLocaleString()}
              </div>
            )}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
                gap: 16,
                marginTop: 10,
              }}
            >
              {baseCards.map((card) => (
                <EditableCard
                  // Remount on a new base version so local state re-seeds.
                  key={`${baseVersion}:${card.id}`}
                  card={card}
                  grid={grid}
                  onEdit={handleEdit}
                />
              ))}
            </div>
          </section>

          {(catalogues ?? []).slice(1).map((cat) => (
            <ReadOnlySection
              key={cat.version}
              title={`Version ${cat.version}`}
              subtitle={
                cat.updatedAt
                  ? `created ${new Date(cat.updatedAt).toLocaleString()}`
                  : undefined
              }
              cards={[...cat.winCards, ...cat.regularCards]}
            />
          ))}

          {latest && (
            <ReadOnlySection
              title="Default fallback cards"
              subtitle="bundled code constants"
              cards={[...DEFAULT_WIN_CARDS, ...DEFAULT_REGULAR_CARDS]}
            />
          )}
        </>
      )}
    </div>
  );
}

const btn: CSSProperties = {
  padding: "2px 10px",
  border: "1px solid #ccc3ac",
  borderRadius: 6,
  background: "#f6f2e6",
  color: "#2b2620",
  fontSize: 12,
  cursor: "pointer",
};

const numInput: CSSProperties = {
  width: 52,
  padding: "3px 6px",
  borderRadius: 6,
  fontSize: 13,
};
